import React, { useState } from 'react';
import styled from 'styled-components';
import SectionBlock from '../sheet/SectionBlock';
import PanchaAngaSection from '../sheet/PanchaAngaSection';
import SunMoonTimesSection from '../sheet/SunMoonTimesSection';
import InauspiciousPeriodsSection from '../sheet/InauspiciousPeriodsSection';
import AuspiciousPeriodsSection from '../sheet/AuspiciousPeriodsSection';
import GowriPanchangamSection from '../sheet/GowriPanchangamSection';
import useLocale from '../../hooks/useLocale';

/*
 * DetailedValuesToggle — refactored for #194 (Risk 6 audit fired).
 *
 * Previously imported 4 deletion-target timeline components. Now consumes
 * the new sheet section components inside SectionBlock wrappers, mirroring
 * the main sheet layout for visual consistency between single-siddhanta
 * and compare-mode "Show Details" expansion.
 */

const ToggleButton = styled.button`
  background: none;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.radius.md}px;
  padding: ${p => p.theme.spacing[2]}px ${p => p.theme.spacing[4]}px;
  color: ${p => p.theme.colors.primary};
  font-size: ${p => p.theme.typography.sizes.sm};
  cursor: pointer;
  margin: ${p => p.theme.spacing[4]}px auto;
  display: block;

  &:hover { border-color: ${p => p.theme.colors.primary}; }
`;

const SectionHeading = styled.h3`
  font-size: ${p => p.theme.typography.sizes.lg};
  color: ${p => p.theme.colors.text.primary};
  text-align: center;
  margin: ${p => p.theme.spacing[5]}px 0 ${p => p.theme.spacing[4]}px;
  padding-bottom: ${p => p.theme.spacing[2]}px;
  border-bottom: 2px solid ${p => p.theme.colors.border};
`;

const StackContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing[3]}px;
`;

function DailySiddhantaStack({ data, now, lang }) {
  if (!data) return null;
  return (
    <StackContainer>
      <SectionBlock titleTa="பஞ்சாங்கம்" titleEn="Pancha Anga">
        <PanchaAngaSection fiveLimbs={data.five_limbs} now={now} lang={lang} />
      </SectionBlock>
      <SectionBlock titleTa="சூரிய சந்திர நேரம்" titleEn="Sun/Moon Times">
        <SunMoonTimesSection sunMoon={data.sun_moon} lang={lang} />
      </SectionBlock>
      <SectionBlock titleTa="அசுப காலம்" titleEn="Inauspicious" tone="red">
        <InauspiciousPeriodsSection inauspicious={data.inauspicious} now={now} lang={lang} />
      </SectionBlock>
      <SectionBlock titleTa="சுப காலம்" titleEn="Auspicious" tone="green">
        <AuspiciousPeriodsSection auspicious={data.auspicious} now={now} lang={lang} />
      </SectionBlock>
      <SectionBlock titleTa="கௌரி பஞ்சாங்கம்" titleEn="Gowri Panchangam" fullWidth>
        <GowriPanchangamSection gowri={data.gowri} now={now} lang={lang} />
      </SectionBlock>
    </StackContainer>
  );
}

const DetailedValuesToggle = ({ drik, vakyam, now, lang = 'en' }) => {
  const [expanded, setExpanded] = useState(false);
  const t = useLocale(lang, 'comparison');

  return (
    <>
      <ToggleButton
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        {expanded
          ? (t.detailed?.collapse || 'Hide full panchangam')
          : (t.detailed?.expand || 'Show full panchangam for both siddhantas')}
      </ToggleButton>

      {expanded && (
        <>
          <SectionHeading>{t.detailed?.drikHeading || 'Thirukanitha (Drik)'}</SectionHeading>
          <DailySiddhantaStack data={drik} now={now} lang={lang} />

          <SectionHeading>{t.detailed?.vakyamHeading || 'Vakyam (Surya Siddhanta)'}</SectionHeading>
          <DailySiddhantaStack data={vakyam} now={now} lang={lang} />
        </>
      )}
    </>
  );
};

export default DetailedValuesToggle;
